import { format, parseISO } from 'date-fns';
import { sortBy } from 'lodash';

import { Box } from 'components/box/Box';
import { isPslf } from 'utils/constants';

type PslfSignerRole = 'borrower' | 'employer';

type PslfSignRequest = {
  role: PslfSignerRole;
  complete: boolean;
  updatedAt: string;
  pslfTemplateKind?: string;
};

export type PslfSignersForm = {
  formType: string;
  signRequests: PslfSignRequest[];
};

const ROLE_RANK: Record<PslfSignerRole, number> = {
  borrower: 0,
  employer: 1,
};

const ROLE_LABEL: Record<PslfSignerRole, string> = {
  borrower: 'Borrower',
  employer: 'Employer',
};

const TEMPLATE_LABEL: ReadonlyMap<string, string> = new Map([
  ['ssn', 'SSN Prefilled'],
  ['ssnWithApproverPrefill', 'SSN with approver prefill'],
  ['legacy', 'Legacy'],
]);

const UNKNOWN_TEMPLATE_LABEL = 'Unknown';

const templateLabel = (pslfTemplateKind: string | undefined) =>
  pslfTemplateKind === undefined
    ? '-'
    : (TEMPLATE_LABEL.get(pslfTemplateKind) ?? UNKNOWN_TEMPLATE_LABEL);

const headerCellClasses = 'text-left p-3 font-semibold';
const bodyCellClasses = 'text-left p-3';

const PslfSigners = ({ form }: { form: PslfSignersForm }) => {
  const { formType, signRequests } = form;

  if (!isPslf(formType) || signRequests.length === 0) {
    return null;
  }

  const signers = sortBy(signRequests, ({ role }) => ROLE_RANK[role]);
  const showTemplate = signers.some(
    ({ pslfTemplateKind }) => pslfTemplateKind !== undefined,
  );

  return (
    <Box
      border="solid 1px"
      borderRadius={6}
      borderColor="blueGrey"
      p={24}
      overflowX="auto"
    >
      <table className="w-full border-collapse">
        <caption className="text-left font-semibold pb-3">Signers</caption>
        <thead>
          <tr className="border-b">
            <th className={headerCellClasses} scope="col">
              Role
            </th>
            <th className={headerCellClasses} scope="col">
              Status
            </th>
            <th className={headerCellClasses} scope="col">
              Signed Date
            </th>
            {showTemplate && (
              <th className={headerCellClasses} scope="col">
                Template
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {signers.map(({ role, complete, updatedAt, pslfTemplateKind }) => (
            <tr key={role} className="border-b">
              <th className={`${bodyCellClasses} font-normal`} scope="row">
                {ROLE_LABEL[role]}
              </th>
              <td className={bodyCellClasses}>
                {complete ? 'Signed' : 'Pending'}
              </td>
              <td className={bodyCellClasses}>
                {complete ? format(parseISO(updatedAt), 'LLL d, yyyy') : '-'}
              </td>
              {showTemplate && (
                <td className={bodyCellClasses}>
                  {templateLabel(pslfTemplateKind)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
};

export default PslfSigners;
